/**
 * File list hook for managing workspace files.
 *
 * Blueprint: specs/013-file-services/spec.md
 */

import { useState, useCallback, useEffect } from 'react';
import { getAuthToken } from '../context/AuthContext';

const AGENT_URL = process.env.REACT_APP_AGENT_URL || 'http://localhost:8090';

export interface FileInfo {
  name: string;
  path: string;
  size: number;
  isDir: boolean;
  modifiedAt: string;
  mimeType: string | null;
}

interface UseFileListOptions {
  sessionId: string;
  userId: string;
  agentEndpoint?: string;
  autoFetch?: boolean;
}

interface UseFileListReturn {
  files: FileInfo[];
  loading: boolean;
  error: string | null;
  fetchFiles: () => Promise<void>;
  deleteFile: (path: string) => Promise<boolean>;
  deleteFiles: (paths: string[]) => Promise<{ deleted: string[]; errors: string[] }>;
  downloadFile: (path: string, filename: string) => Promise<void>;
  refreshFiles: () => Promise<void>;
}

export function useFileList({
  sessionId,
  userId,
  agentEndpoint = AGENT_URL,
  autoFetch = true,
}: UseFileListOptions): UseFileListReturn {
  const [files, setFiles] = useState<FileInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchFiles = useCallback(async () => {
    if (!sessionId || !userId) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${agentEndpoint}/files/list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(getAuthToken() ? { Authorization: `Bearer ${getAuthToken()}` } : {}) },
        body: JSON.stringify({
          session_id: sessionId,
          user_id: userId,
          path: '/workspace/',
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch files: ${errorText}`);
      }

      const data = await response.json();
      const fileList: FileInfo[] = (data.files || []).map((f: Record<string, unknown>) => ({
        name: f.name as string,
        path: f.path as string,
        size: f.size as number,
        isDir: f.is_dir as boolean,
        modifiedAt: f.modified_at as string,
        mimeType: f.mime_type as string | null,
      }));

      setFiles(fileList);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch files');
    } finally {
      setLoading(false);
    }
  }, [sessionId, userId, agentEndpoint]);

  const deleteFile = useCallback(
    async (path: string): Promise<boolean> => {
      if (!sessionId || !userId) {
        return false;
      }

      try {
        const response = await fetch(`${agentEndpoint}/files/delete`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(getAuthToken() ? { Authorization: `Bearer ${getAuthToken()}` } : {}) },
          body: JSON.stringify({
            session_id: sessionId,
            user_id: userId,
            paths: [path],
          }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to delete file: ${errorText}`);
        }

        const data = await response.json();
        if (data.deleted && data.deleted.length > 0) {
          // Remove deleted files from state using server's deleted paths
          const deletedSet = new Set(data.deleted);
          setFiles((prev) => prev.filter((f) => !deletedSet.has(f.path)));
          return true;
        }

        // If nothing was deleted but no error, still consider it success
        return data.success || false;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to delete file');
        return false;
      }
    },
    [sessionId, userId, agentEndpoint]
  );

  const deleteFiles = useCallback(
    async (paths: string[]): Promise<{ deleted: string[]; errors: string[] }> => {
      if (!sessionId || !userId) {
        return { deleted: [], errors: ['Missing session or user ID'] };
      }

      try {
        const response = await fetch(`${agentEndpoint}/files/delete`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(getAuthToken() ? { Authorization: `Bearer ${getAuthToken()}` } : {}) },
          body: JSON.stringify({
            session_id: sessionId,
            user_id: userId,
            paths,
          }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to delete files: ${errorText}`);
        }

        const data = await response.json();
        if (data.deleted && data.deleted.length > 0) {
          setFiles((prev) => prev.filter((f) => !data.deleted.includes(f.path)));
        }

        return { deleted: data.deleted || [], errors: data.errors || [] };
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Failed to delete files';
        setError(errorMsg);
        return { deleted: [], errors: [errorMsg] };
      }
    },
    [sessionId, userId, agentEndpoint]
  );

  const downloadFile = useCallback(
    async (path: string, filename: string): Promise<void> => {
      if (!sessionId || !userId) {
        return;
      }

      try {
        const response = await fetch(`${agentEndpoint}/files/download`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(getAuthToken() ? { Authorization: `Bearer ${getAuthToken()}` } : {}) },
          body: JSON.stringify({
            session_id: sessionId,
            user_id: userId,
            path,
          }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to download file: ${errorText}`);
        }

        // Create blob and trigger download. Never let the download name be empty
        // (an empty/undefined name makes the browser fall back to the blob: URL's
        // UUID). Prefer the server's Content-Disposition, then the passed name,
        // then the path basename.
        const blob = await response.blob();
        const headerName = (response.headers.get('content-disposition') || '').match(
          /filename="?([^"]+)"?/
        )?.[1];
        const safeName = headerName || filename || path.split('/').filter(Boolean).pop() || 'download';
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = safeName;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        }, 0);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to download file');
      }
    },
    [sessionId, userId, agentEndpoint]
  );

  const refreshFiles = useCallback(async () => {
    await fetchFiles();
  }, [fetchFiles]);

  // Clear files when sessionId changes to prevent showing stale data
  useEffect(() => {
    setFiles([]);
    setError(null);
  }, [sessionId]);

  // Auto-fetch on mount if enabled
  useEffect(() => {
    if (autoFetch && sessionId && userId) {
      fetchFiles();
    }
  }, [autoFetch, sessionId, userId, fetchFiles]);

  return {
    files,
    loading,
    error,
    fetchFiles,
    deleteFile,
    deleteFiles,
    downloadFile,
    refreshFiles,
  };
}
